/**
 * Settings (§13.3).
 *
 * Three editors, each with a live preview against the user's real data: email
 * connection and sync history, sender mappings with an address tester, and
 * transaction rules with a match preview. The previews aren't decoration —
 * every one of these settings changes a number somewhere else in the app, and
 * a setting whose effect you can't see before saving is a setting you get wrong.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { EmailPanel } from "@/components/settings/EmailPanel";
import { RulesPanel } from "@/components/settings/RulesPanel";
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  Panel, PageHeader } from "@/components/ui/primitives";
import { sendersApi, type SenderKind, type SenderMapping } from "@/lib/api/client";

export function SettingsPage() {
  return (
    <div className="max-w-4xl space-y-8">
      <PageHeader
        eyebrow="Configuration"
        title="Settings"
        description="Cards, senders, and rules are all data — edit them here, never in code."
      />

      <EmailPanel />

      <SenderMappings />

      <RulesPanel />
    </div>
  );
}

const KINDS: { value: SenderKind; label: string; help: string }[] = [
  { value: "statement", label: "Statement", help: "Monthly statement PDFs." },
  { value: "alert", label: "Alert", help: "Per-transaction alerts." },
  { value: "other", label: "Other", help: "Recognised, but ignored." },
];

function SenderMappings() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [testAddress, setTestAddress] = useState("");

  const { data: mappings, isLoading } = useQuery({
    queryKey: ["senders"],
    queryFn: sendersApi.list,
  });

  const { data: testResult } = useQuery({
    queryKey: ["senders", "test", testAddress],
    queryFn: () => sendersApi.test(testAddress),
    enabled: testAddress.includes("@"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => sendersApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["senders"] });
      toast.success("Sender mapping removed.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Panel className="overflow-hidden">
      <div className="flex items-start justify-between gap-4 p-5">
        <div>
          <h2 className="font-display text-base font-medium text-ink">Sender mappings</h2>
          <p className="mt-1 text-sm text-muted">
            Which address belongs to which bank, and whether it carries statements or
            alerts. Mail from an unrecognised sender is flagged for review, never dropped.
          </p>
        </div>
        <Button variant="default" size="sm" onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" />
          Add
        </Button>
      </div>

      {adding && <SenderForm onDone={() => setAdding(false)} />}

      {isLoading ? (
        <div className="space-y-2 p-5 pt-0">
          <div className="skeleton h-10 w-full" />
          <div className="skeleton h-10 w-full" />
        </div>
      ) : !mappings?.length ? (
        <EmptyState
          icon={Mail}
          title="No senders mapped"
          body="Add the address your bank sends statements from. Co-branded cards send from the issuing bank's domain — map the bank, not the brand."
          action={
            <Button variant="default" size="sm" onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" />
              Add a sender
            </Button>
          }
        />
      ) : (
        <ul className="divide-y divide-hairline border-t border-hairline">
          {mappings.map((m) => (
            <SenderRow key={m.id} mapping={m} onRemove={() => remove.mutate(m.id)} />
          ))}
        </ul>
      )}

      {/* Live test — §13.3 asks for "test against recent transactions" on rules;
          the sender equivalent is showing exactly where an address would route. */}
      {mappings && mappings.length > 0 && (
        <div className="border-t border-hairline bg-base/40 p-5">
          <Field
            label="Test an address"
            hint="Paste an address to see which mapping would catch it."
          >
            <Input
              value={testAddress}
              onChange={(e) => setTestAddress(e.target.value)}
              placeholder="statements@yourbank.example"
            />
          </Field>
          {testResult && (
            <p
              className={`mt-2 text-xs ${testResult.matched ? "text-paid" : "text-warn"}`}
            >
              {testResult.explanation}
            </p>
          )}
        </div>
      )}
    </Panel>
  );
}

function SenderRow({
  mapping,
  onRemove,
}: {
  mapping: SenderMapping;
  onRemove: () => void;
}) {
  const tone = mapping.kind === "statement" ? "brass" : mapping.kind === "alert" ? "neutral" : "neutral";
  return (
    <li className="flex items-center gap-3 px-5 py-3">
      <div className="min-w-0 flex-1">
        <div className="truncate font-mono text-sm text-ink">{mapping.address_pattern}</div>
        <div className="text-xs text-faint">{mapping.issuer}</div>
      </div>
      <Badge tone={tone as "brass" | "neutral"}>{mapping.kind}</Badge>
      {!mapping.active && <Badge tone="neutral">Off</Badge>}
      <Button variant="ghost" size="icon" onClick={onRemove} aria-label="Remove mapping">
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  );
}

function SenderForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    address_pattern: "",
    issuer: "",
    kind: "statement" as SenderKind,
  });
  const [error, setError] = useState<string>();

  const create = useMutation({
    mutationFn: () => sendersApi.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["senders"] });
      toast.success("Sender mapped.");
      onDone();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <form
      className="space-y-4 border-t border-hairline bg-base/40 p-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!form.address_pattern.includes("@")) {
          setError("Needs to look like an address or pattern, e.g. *@yourbank.example");
          return;
        }
        if (!form.issuer.trim()) {
          setError("Which bank is this?");
          return;
        }
        create.mutate();
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Address pattern" hint="* is a wildcard.">
          <Input
            value={form.address_pattern}
            onChange={(e) => setForm((f) => ({ ...f, address_pattern: e.target.value }))}
            placeholder="*@yourbank.example"
            autoFocus
          />
        </Field>
        <Field label="Issuer" hint="The bank behind the card.">
          <Input
            value={form.issuer}
            onChange={(e) => setForm((f) => ({ ...f, issuer: e.target.value }))}
          />
        </Field>
      </div>

      <Field label="Kind">
        <div className="flex flex-wrap gap-2">
          {KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              title={k.help}
              onClick={() => setForm((f) => ({ ...f, kind: k.value }))}
              className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                form.kind === k.value
                  ? "border-brass/50 bg-brass/10 text-brass"
                  : "border-hairline text-muted hover:text-ink"
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>
      </Field>

      {error && <p className="text-xs text-overdue">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={create.isPending}>
          {create.isPending ? "Saving…" : "Add mapping"}
        </Button>
      </div>
    </form>
  );
}
